/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The role line on the profile: one pill for the highest hat someone wears at
 * their gym, and the legacy claim label for everyone else.
 */

import React, { useEffect, useState } from "react";
import { getBox } from "@lemonadetraining/web-shared";
import RolePill from "../Box/People/RolePill";
import { useUserAccess } from "../../hooks/useUserAccess";

/*
 * ProfileInfo renders this in both its desktop and mobile layouts, so both
 * mount and only CSS hides one. Cached per gym so that costs one read, not two.
 * (useBoxOwnerUID caches the same document for ownerUID; folding the two into
 * one shared box cache is worth a follow-up.)
 */
const gymNameByBox = new Map<string, Promise<string | null>>();

const loadGymName = (boxID: string): Promise<string | null> => {
  const cached = gymNameByBox.get(boxID);
  if (cached) return cached;
  const pending = getBox(boxID)
    .then((box: { name?: string } | null) => box?.name ?? null)
    .catch(() => {
      gymNameByBox.delete(boxID);
      return null;
    });
  gymNameByBox.set(boxID, pending);
  return pending;
};

export const _resetGymNameCacheForTests = (): void => {
  gymNameByBox.clear();
};

const ProfileRoleLine: React.FC<{ claimRole?: string }> = ({ claimRole }) => {
  const { displayRole: hat, boxID } = useUserAccess();
  const [gymName, setGymName] = useState<string | null>(null);

  useEffect(() => {
    if (!hat || !boxID) return undefined;
    let active = true;
    loadGymName(boxID).then((name) => {
      if (active) setGymName(name);
    });
    return () => {
      active = false;
    };
  }, [hat, boxID]);

  if (!hat) {
    return (
      <p className="text-sm text-muted-foreground capitalize" data-testid="profile-role-line">
        Role: {claimRole || "unknown"}
      </p>
    );
  }

  // A div, not a p: the pill is a Badge element and cannot legally nest in one.
  return (
    <div
      className="flex items-center gap-2 text-sm text-muted-foreground"
      data-testid="profile-role-line"
    >
      <RolePill role={hat} />
      {gymName ? <span>at {gymName}</span> : null}
    </div>
  );
};

export default ProfileRoleLine;
